<script setup>
import axios from 'axios'
import {
    computed,
    nextTick,
    onBeforeUnmount,
    onMounted,
    ref,
    shallowRef
} from 'vue'
import { useI18n } from 'vue-i18n'
import { languages } from '@/i18n'
import { parseHeziyunData } from '@/utils/heziyunData'
import Loading from '@/components/main/loading.vue'
import LocaleSwitch from '@/components/main/LocaleSwitch.vue'
import selectedIcon from '@/static/img/header/hot.webp' // 三级精选图片：按你项目现有文件名修改这一行即可

const EMPTY_LIST = []
const DROPDOWN_CLOSE_DELAY = 140
const DESKTOP_MEDIA_QUERY = '(min-width: 769px)'

const isClient = typeof window !== 'undefined'
const normalizeId = value => String(value ?? '')
const isInvalidLink = url => !url || url === '#' || url === '##'
const isRecommended = item => item?.recommend === 1 || item?.recommend === '1'

const readStorage = key => {
    if (!isClient) return ''

    try {
        return window.localStorage.getItem(key) || ''
    } catch {
        return ''
    }
}

const removeStorage = key => {
    if (!isClient) return

    try {
        window.localStorage.removeItem(key)
    } catch {
        // 禁用或受限的 localStorage 环境下忽略清理失败
    }
}

const writeStorage = (key, value) => {
    if (!isClient) return

    try {
        window.localStorage.setItem(key, value)
    } catch {
        // 禁用或受限的 localStorage 环境下忽略写入失败
    }
}

const sortData = data => {
    if (!Array.isArray(data) || data.length < 2) {
        return Array.isArray(data) ? data.slice() : []
    }

    return data.slice().sort((a, b) => {
        const recommendDiff = Number(isRecommended(b)) - Number(isRecommended(a))

        if (recommendDiff) return recommendDiff
        if (isRecommended(a)) return Number(a.id || 0) - Number(b.id || 0)

        return 0
    })
}

/**
 * 菜单只在初始化时完成分组、排序和字段标准化。
 * 模板渲染阶段直接读取 children/products，避免反复 filter + sort。
 */
const buildNavigation = data => {
    const productsBySecondId = Object.create(null)
    const secondItemsByNavId = Object.create(null)

    // 三级产品：保留原始字段，同时统一补充菜单状态字段
    sortData(data?.head).forEach((item, index) => {
        const parentKey = normalizeId(item?.typeid)

        if (!parentKey) return
        if (!productsBySecondId[parentKey]) productsBySecondId[parentKey] = []

        productsBySecondId[parentKey].push({
            ...item,
            _key: normalizeId(item?.id) || `product-${parentKey}-${index}`,
            isRecommended: isRecommended(item),
            hasTips: Boolean(item?.tips),
            hasUrl: !isInvalidLink(item?.url)
        })
    })

    // 二级分类：补齐 HOT、style、是否有产品等状态
    sortData(data?.headType).forEach((item, index) => {
        const parentKey = normalizeId(item?.types)
        const itemKey = normalizeId(item?.id) || `second-${parentKey}-${index}`

        if (!parentKey) return
        if (!secondItemsByNavId[parentKey]) secondItemsByNavId[parentKey] = []

        const products = productsBySecondId[itemKey] || EMPTY_LIST

        secondItemsByNavId[parentKey].push({
            ...item,
            _key: itemKey,
            products,
            isRecommended: isRecommended(item),
            styleType: Number(item?.style || 1),
            hasProducts: products.length > 0
        })
    })

    // 一级菜单：HOT、下拉箭头、是否存在下级菜单
    const source = Array.isArray(data?.headTypeNew) ? data.headTypeNew : []

    return source.map((item, index) => {
        const itemKey = normalizeId(item?.id) || `nav-${index}`
        const children = secondItemsByNavId[itemKey] || EMPTY_LIST
        const showArrow = Boolean(Number(item?.isJump))

        return {
            ...item,
            _key: itemKey,
            children,
            showArrow,
            hasChildren: showArrow && children.length > 0,
            isRecommended: isRecommended(item)
        }
    })
}

const languageOptions = languages.map(item => ({
    ...item,
    code: String(item.value).toUpperCase()
}))
const languageMap = new Map(languageOptions.map(item => [item.value, item]))

const initialData = isClient ? (parseHeziyunData() || {}) : {}
const HeziyunData = shallowRef(initialData)
const navItems = shallowRef(buildNavigation(initialData))
const jwt = ref(readStorage('jwt'))
const userInfo = shallowRef(null)

const currentActiveIndex = ref(null)
const hoverActiveIndex = ref(null)
const dropdownNavIndex = ref(null)
const activeSecondId = ref('')
const dropdownVisible = ref(false)
const isHeaderScrolled = ref(false)

// PC 下拉菜单定位：根据当前悬停的一级菜单动态计算 left
const headerRef = ref(null)
const dropdownRef = ref(null)
const dropdownLeft = ref(12)

const mobileMenuVisible = ref(false)
const mobileLanguageVisible = ref(false)
const mobileExpandedNavId = ref('')
const mobileExpandedSecondId = ref('')

const { locale } = useI18n({ useScope: 'global' })
const currentLocale = locale
const mobileLanguageDrawerHeight = `${Math.min(
    460,
    126 + Math.max(languageOptions.length, 1) * 58
)}px`

let dropdownCloseTimer = null
let dropdownPositionFrame = null
let headerScrollFrame = null
let headerResizeObserver = null
let desktopMediaQuery = null
let activeNavElement = null
let isUnmounted = false

const currentLanguageLabel = computed(() => {
    return languageMap.get(locale.value)?.label
        || languageOptions[0]?.label
        || '语言'
})

const displayedActiveIndex = computed(() => {
    if (hoverActiveIndex.value !== null) return hoverActiveIndex.value

    if (dropdownVisible.value && dropdownNavIndex.value !== null) {
        return dropdownNavIndex.value
    }

    return currentActiveIndex.value
})

const dropdownSecondItems = computed(() => {
    if (dropdownNavIndex.value === null) return EMPTY_LIST

    return navItems.value[dropdownNavIndex.value]?.children || EMPTY_LIST
})

const activeSecondItem = computed(() => {
    return dropdownSecondItems.value.find(item => item._key === activeSecondId.value)
})

const dropdownProductItems = computed(() => {
    return activeSecondItem.value?.products || EMPTY_LIST
})

// 当前二级分类的展示样式，兼容旧 Header 的 style=1 / style=2 数据状态
const activeSecondStyleType = computed(() => {
    return activeSecondItem.value?.styleType || 1
})

const dropdownPositionStyle = computed(() => ({
    left: `${dropdownLeft.value}px`
}))

const isLoggedIn = computed(() => Boolean(jwt.value && userInfo.value))
const logoUrl = computed(() => HeziyunData.value?.global?.logo1 || '')
const avatarUrl = computed(() => {
    return userInfo.value?.email
        ? `https://q1.qlogo.cn/g?b=qq&nk=${encodeURIComponent(userInfo.value.email)}&s=100`
        : ''
})
const userInitial = computed(() => userInfo.value?.username?.charAt(0) || 'U')

const updateHeaderScrollState = () => {
    if (!isClient) return

    const nextState = Boolean(
        window.scrollY || document.documentElement.scrollTop
    )

    if (nextState !== isHeaderScrolled.value) {
        isHeaderScrolled.value = nextState
    }
}

const handleWindowScroll = () => {
    if (headerScrollFrame !== null) return

    headerScrollFrame = window.requestAnimationFrame(() => {
        updateHeaderScrollState()
        headerScrollFrame = null
    })
}

const normalizePath = (url = '') => {
    if (isInvalidLink(url)) return ''

    try {
        return new URL(url, window.location.origin)
            .pathname
            .replace(/\/+$/, '') || '/'
    } catch {
        return String(url)
            .split(/[?#]/)[0]
            .replace(/\/+$/, '') || '/'
    }
}

const resolveCurrentNav = () => {
    const currentPath = normalizePath(window.location.pathname)
    const index = navItems.value.findIndex(item => {
        const itemPath = normalizePath(item.url)

        if (!itemPath) return false
        if (itemPath === '/') return currentPath === '/'

        return currentPath === itemPath || currentPath.startsWith(`${itemPath}/`)
    })

    currentActiveIndex.value = index < 0 ? null : index
}

const cancelDropdownClose = () => {
    if (dropdownCloseTimer === null) return

    window.clearTimeout(dropdownCloseTimer)
    dropdownCloseTimer = null
}

const hideDropdownContent = () => {
    dropdownVisible.value = false
    dropdownNavIndex.value = null
    activeSecondId.value = ''
}

const closeDropdown = () => {
    cancelDropdownClose()
    hideDropdownContent()
    hoverActiveIndex.value = null
    activeNavElement = null
}

const scheduleDropdownClose = () => {
    cancelDropdownClose()
    dropdownCloseTimer = window.setTimeout(closeDropdown, DROPDOWN_CLOSE_DELAY)
}

/**
 * 让 PC 下拉菜单跟随当前一级菜单：
 * 1. 默认让下拉菜单中心对齐一级菜单中心；
 * 2. 靠近左右边缘时自动夹紧，避免超出头部。
 */
const updateDropdownPosition = async (navElement = activeNavElement) => {
    if (!isClient || !navElement || window.innerWidth <= 768) return

    await nextTick()

    const headerElement = headerRef.value
    const dropdownElement = dropdownRef.value

    if (
        isUnmounted
        || navElement !== activeNavElement
        || !dropdownVisible.value
        || !headerElement
        || !dropdownElement
    ) return

    const headerRect = headerElement.getBoundingClientRect()
    const navRect = navElement.getBoundingClientRect()
    const edgeGap = 12
    const panelWidth = dropdownElement.offsetWidth

    const navCenterInHeader =
        navRect.left - headerRect.left + navRect.width / 2

    const idealLeft = navCenterInHeader - panelWidth / 2
    const maxLeft = Math.max(
        edgeGap,
        headerRect.width - panelWidth - edgeGap
    )

    dropdownLeft.value = Math.round(
        Math.min(Math.max(idealLeft, edgeGap), maxLeft)
    )
}

const scheduleDropdownPositionUpdate = () => {
    if (
        !isClient
        || !dropdownVisible.value
        || !activeNavElement
        || dropdownPositionFrame !== null
    ) return

    dropdownPositionFrame = window.requestAnimationFrame(() => {
        dropdownPositionFrame = null
        updateDropdownPosition()
    })
}

const handleNavMouseEnter = async (event, index, item) => {
    cancelDropdownClose()
    hoverActiveIndex.value = index
    activeNavElement = event.currentTarget

    if (!item.hasChildren) {
        hideDropdownContent()
        return
    }

    dropdownNavIndex.value = index

    if (!item.children.some(child => child._key === activeSecondId.value)) {
        activeSecondId.value = item.children[0]._key
    }

    dropdownVisible.value = true
    await updateDropdownPosition(activeNavElement)
}

const handleNavLeave = () => {
    hoverActiveIndex.value = null
}

const preventInvalidLink = (event, item) => {
    if (!isInvalidLink(item?.url)) return false

    event.preventDefault()
    return true
}

const handleNavClick = (event, item, index) => {
    currentActiveIndex.value = index
    preventInvalidLink(event, item)
}

const selectSecondItem = item => {
    cancelDropdownClose()
    activeSecondId.value = item._key
}

const handleProductClick = (event, item) => {
    preventInvalidLink(event, item)
}

const openMobileMenu = () => {
    closeDropdown()
    mobileLanguageVisible.value = false
    mobileMenuVisible.value = true
}

const closeMobileMenu = () => {
    mobileMenuVisible.value = false
    mobileExpandedNavId.value = ''
    mobileExpandedSecondId.value = ''
}

const closeMobilePanels = () => {
    closeMobileMenu()
    mobileLanguageVisible.value = false
}

const toggleMobileNav = item => {
    mobileExpandedNavId.value =
        mobileExpandedNavId.value === item._key ? '' : item._key
    mobileExpandedSecondId.value = ''
}

const toggleMobileSecond = item => {
    // 即使当前二级分类没有产品，也允许展开，展示明确的空状态反馈。
    mobileExpandedSecondId.value =
        mobileExpandedSecondId.value === item._key ? '' : item._key
}

const openMobileLanguageSelector = async () => {
    mobileMenuVisible.value = false
    await nextTick()
    mobileLanguageVisible.value = true
}

const handleMobileLanguageSelect = value => {
    mobileLanguageVisible.value = false

    if (!value || value === locale.value) return

    locale.value = value
    writeStorage('arco-locale', value)
    window.location.reload()
}

const setCollapseStyle = (element, height, opacity, translateY) => {
    element.style.height = height
    element.style.opacity = opacity
    element.style.transform = `translate3d(0, ${translateY}, 0)`
    element.style.willChange = 'height, opacity, transform'
}

const clearCollapseStyle = element => {
    ;['height', 'opacity', 'transform', 'will-change'].forEach(property => {
        element.style.removeProperty(property)
    })
}

const beforeMobileCollapseEnter = element => {
    setCollapseStyle(element, '0px', '0', '-8px')
}

const enterMobileCollapse = element => {
    const targetHeight = element.scrollHeight

    window.requestAnimationFrame(() => {
        setCollapseStyle(element, `${targetHeight}px`, '1', '0')
    })
}

const afterMobileCollapseEnter = element => {
    clearCollapseStyle(element)
    element.style.height = 'auto'
}

const beforeMobileCollapseLeave = element => {
    setCollapseStyle(element, `${element.scrollHeight}px`, '1', '0')
    void element.offsetHeight
}

const leaveMobileCollapse = element => {
    window.requestAnimationFrame(() => {
        setCollapseStyle(element, '0px', '0', '-6px')
    })
}

const afterMobileCollapseLeave = clearCollapseStyle

const handleMobileLinkClick = (event, item) => {
    if (!preventInvalidLink(event, item)) closeMobileMenu()
}

const clearAuth = () => {
    removeStorage('jwt')
    jwt.value = ''
    userInfo.value = null
}

const fetchUserInfo = async () => {
    if (!jwt.value) return

    try {
        const response = await axios.get('/console/v1/account', {
            headers: {
                Authorization: `Bearer ${jwt.value}`
            }
        })

        if (isUnmounted) return

        if (Number(response?.data?.status) === 401) {
            clearAuth()
            return
        }

        userInfo.value = response?.data?.data?.account || null
    } catch (error) {
        if (isUnmounted) return

        const status = Number(
            error?.response?.status || error?.response?.data?.status
        )

        if (status === 401) {
            clearAuth()
            return
        }

        console.error('用户信息请求失败：', error)
    }
}


const handleDesktopMediaChange = event => {
    if (event.matches) closeMobilePanels()
}

const bindHeaderResizeObserver = () => {
    if (!window.ResizeObserver || !headerRef.value) return

    headerResizeObserver = new window.ResizeObserver(() => {
        scheduleDropdownPositionUpdate()
    })
    headerResizeObserver.observe(headerRef.value)
}

const unbindHeaderResizeObserver = () => {
    if (!headerResizeObserver) return

    headerResizeObserver.disconnect()
    headerResizeObserver = null
}

const bindDesktopMediaQuery = () => {
    if (!window.matchMedia) return

    desktopMediaQuery = window.matchMedia(DESKTOP_MEDIA_QUERY)

    if (desktopMediaQuery.addEventListener) {
        desktopMediaQuery.addEventListener('change', handleDesktopMediaChange)
    } else if (desktopMediaQuery.addListener) {
        desktopMediaQuery.addListener(handleDesktopMediaChange)
    }
}

const unbindDesktopMediaQuery = () => {
    if (!desktopMediaQuery) return

    if (desktopMediaQuery.removeEventListener) {
        desktopMediaQuery.removeEventListener('change', handleDesktopMediaChange)
    } else if (desktopMediaQuery.removeListener) {
        desktopMediaQuery.removeListener(handleDesktopMediaChange)
    }

    desktopMediaQuery = null
}

onMounted(() => {
    resolveCurrentNav()
    fetchUserInfo()
    updateHeaderScrollState()
    bindDesktopMediaQuery()
    bindHeaderResizeObserver()

    window.addEventListener('resize', scheduleDropdownPositionUpdate, { passive: true })
    window.addEventListener('scroll', handleWindowScroll, { passive: true })
})

onBeforeUnmount(() => {
    isUnmounted = true
    cancelDropdownClose()
    unbindDesktopMediaQuery()
    unbindHeaderResizeObserver()
    window.removeEventListener('resize', scheduleDropdownPositionUpdate)
    window.removeEventListener('scroll', handleWindowScroll)

    if (dropdownPositionFrame !== null) {
        window.cancelAnimationFrame(dropdownPositionFrame)
        dropdownPositionFrame = null
    }

    if (headerScrollFrame !== null) {
        window.cancelAnimationFrame(headerScrollFrame)
        headerScrollFrame = null
    }
})
</script>

<template>
    <header ref="headerRef" class="hy-header Heziyun-header-superclass" :class="{ 'is-scrolled': isHeaderScrolled }"
        @mouseenter="cancelDropdownClose" @mouseleave="scheduleDropdownClose">
        <div class="hy-header__inner">

            <!-- Logo -->
            <a href="/" class="hy-brand" aria-label="盒子云首页">
                <img v-if="logoUrl" :src="logoUrl" class="hy-brand__image" alt="盒子云">
            </a>

            <!-- 一级导航：不再渲染任何下拉内容 -->
            <nav class="hy-navigation" aria-label="主导航" @mouseleave="handleNavLeave">
                <a v-for="(item, index) in navItems" :key="item._key" :href="item.url || '##'"
                    class="hy-navigation__item" :class="{
                        'is-active': displayedActiveIndex === index
                    }" @mouseenter="handleNavMouseEnter($event, index, item)"
                    @click="handleNavClick($event, item, index)">
                    <span class="hy-navigation__text">
                        {{ item.title }}
                    </span>

                    <!-- 有下级数据的导航只保留箭头视觉，不打开下拉 -->
                    <svg v-if="item.showArrow" class="hy-navigation__arrow" viewBox="0 0 12 12" fill="none"
                        xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <path v-if="displayedActiveIndex === index" d="M2.25 7.5L6 3.75L9.75 7.5" stroke="currentColor"
                            stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" />

                        <path v-else d="M2.25 4.5L6 8.25L9.75 4.5" stroke="currentColor" stroke-width="1.4"
                            stroke-linecap="round" stroke-linejoin="round" />
                    </svg>

                    <span v-if="item.isRecommended" class="hy-navigation__hot">
                        HOT
                    </span>
                </a>
            </nav>

            <!-- 右侧操作区 -->
            <div class="hy-actions">
                <LocaleSwitch />

                <a href="/document.html" class="hy-actions__link hy-document">
                    <svg class="hy-actions__icon" viewBox="0 0 24 24" fill="none"
                        xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <path d="M6 3.75H14.5L18 7.25V20.25H6V3.75Z" stroke="currentColor" stroke-width="1.6"
                            stroke-linejoin="round" />
                        <path d="M14 3.75V7.75H18" stroke="currentColor" stroke-width="1.6"
                            stroke-linejoin="round" />
                        <path d="M8.75 11H15.25M8.75 14.5H15.25" stroke="currentColor" stroke-width="1.6"
                            stroke-linecap="round" />
                    </svg>
                    <span>{{ $t('common.documentationCenter') }}</span>
                </a>

                <a href="/home.htm" class="hy-actions__link hy-console">
                    <svg class="hy-actions__icon" viewBox="0 0 24 24" fill="none"
                        xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <rect x="3.75" y="4.5" width="16.5" height="12" rx="1.5" stroke="currentColor"
                            stroke-width="1.6" />
                        <path d="M9 20H15M12 16.5V20" stroke="currentColor" stroke-width="1.6"
                            stroke-linecap="round" />
                        <path d="M7 9L9 11L7 13M11.5 13H15.5" stroke="currentColor" stroke-width="1.6"
                            stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                    <span>{{ $t('common.console') }}</span>
                </a>

                <template v-if="!isLoggedIn">
                    <a href="/login.htm" class="hy-actions__link hy-login">
                        <svg class="hy-actions__icon" viewBox="0 0 24 24" fill="none"
                            xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <path
                                d="M9.5 4.25H6.25C5.42 4.25 4.75 4.92 4.75 5.75V18.25C4.75 19.08 5.42 19.75 6.25 19.75H9.5"
                                stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
                            <path d="M13 8L17 12L13 16M8.5 12H17" stroke="currentColor" stroke-width="1.6"
                                stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                        <span>{{ $t('common.signIn') }}</span>
                    </a>

                    <a href="/regist.htm" class="hy-register">
                        <svg class="hy-register__icon" viewBox="0 0 20 20" fill="none"
                            xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <path d="M3.25 8.25H16.75V16.75H3.25V8.25Z" stroke="currentColor" stroke-width="1.5"
                                stroke-linejoin="round" />

                            <path d="M2.5 5.25H17.5V8.25H2.5V5.25Z" stroke="currentColor" stroke-width="1.5"
                                stroke-linejoin="round" />

                            <path d="M10 5.25V16.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />

                            <path
                                d="M9.9 5.1C8.2 5.1 5.5 4.65 5.5 2.9C5.5 1.95 6.25 1.35 7.15 1.35C8.65 1.35 9.55 3.25 9.9 5.1Z"
                                stroke="currentColor" stroke-width="1.35" stroke-linejoin="round" />

                            <path
                                d="M10.1 5.1C11.8 5.1 14.5 4.65 14.5 2.9C14.5 1.95 13.75 1.35 12.85 1.35C11.35 1.35 10.45 3.25 10.1 5.1Z"
                                stroke="currentColor" stroke-width="1.35" stroke-linejoin="round" />
                        </svg>

                        <span>{{ $t('common.register') }}</span>
                    </a>
                </template>

                <!-- 已登录状态 -->
                <a v-else href="/account.htm" class="hy-user" aria-label="进入用户中心">
                    <img v-if="avatarUrl" :src="avatarUrl" class="hy-user__avatar" alt="">

                    <span v-else class="hy-user__avatar hy-user__avatar--text">
                        {{ userInitial }}
                    </span>

                    <span class="hy-user__name">
                        {{ userInfo?.username }}
                    </span>
                </a>
            </div>

            <!-- 移动端：注册/用户入口 + 汉堡菜单 -->
            <div class="hy-mobile-actions">
                <a v-if="!isLoggedIn" href="/regist.htm" class="hy-mobile-register">
                    {{ $t('common.register') }}
                </a>

                <a v-else href="/account.htm" class="hy-mobile-user" aria-label="进入用户中心">
                    <img v-if="avatarUrl" :src="avatarUrl" alt="">
                    <span v-else>{{ userInitial }}</span>
                </a>

                <button type="button" class="hy-mobile-menu-button" aria-label="打开导航菜单"
                    :aria-expanded="mobileMenuVisible" @click="openMobileMenu">
                    <span></span>
                    <span></span>
                    <span></span>
                </button>
            </div>
        </div>
        <transition name="hy-dropdown">
            <section v-if="dropdownVisible && dropdownSecondItems.length" ref="dropdownRef"
                class="hy-mega-menu" :style="dropdownPositionStyle"
                @mouseenter="cancelDropdownClose" @mouseleave="scheduleDropdownClose">
                <!-- 左侧二级分类 -->
                <div class="hy-mega-menu__aside">
                    <div class="hy-mega-menu__category-list">
                        <button v-for="item in dropdownSecondItems" :key="item._key" type="button"
                            class="hy-mega-menu__category" :class="[
                                {
                                    'is-active': item._key === activeSecondId,
                                    'is-recommended': item.isRecommended
                                },
                                `is-style-${item.styleType}`
                            ]" @mouseenter="selectSecondItem(item)" @focus="selectSecondItem(item)"
                            @click="selectSecondItem(item)">

                            <span class="hy-mega-menu__category-title">
                                <span class="hy-mega-menu__category-text">{{ item.title }}</span>
                                <em v-if="item.isRecommended" class="hy-mega-menu__hot">HOT</em>
                            </span>

                            <svg class="hy-mega-menu__category-arrow" viewBox="0 0 16 16" fill="none"
                                xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                <path d="M6 3.5L10.5 8L6 12.5" stroke="currentColor" stroke-width="1.5"
                                    stroke-linecap="round" stroke-linejoin="round" />
                            </svg>
                        </button>
                    </div>
                </div>

                <!-- 右侧产品区域 -->
                <transition name="hy-products" mode="out-in">
                    <div class="hy-mega-menu__ceontent" :key="activeSecondId"
                        :class="`is-style-${activeSecondStyleType}`">
                        <div class="hy-mega-menu__products">
                            <a v-for="item in dropdownProductItems" :key="item._key" :href="item.url || '##'"
                                class="hy-mega-menu__product" :class="{ 'is-recommended': item.isRecommended }"
                                @click="handleProductClick($event, item)">

                                <span class="hy-mega-menu__product-content">
                                    <span class="hy-mega-menu__product-heading">
                                        <strong class="hy-mega-menu__product-title">
                                            {{ item.title }}
                                        </strong>
                                        <img v-if="item.isRecommended" :src="selectedIcon"
                                            class="hy-mega-menu__selected-image" alt="" aria-hidden="true">
                                    </span>

                                    <span v-if="item.hasTips" class="hy-mega-menu__product-description">
                                        {{ item.tips }}
                                    </span>
                                </span>
                            </a>

                            <div v-if="dropdownProductItems.length === 0" class="hy-mega-menu__empty">
                                当前分类暂未配置产品
                            </div>
                        </div>
                    </div>
                </transition>
            </section>
        </transition>
    </header>

    <!-- Arco Design：移动端右侧导航抽屉 -->
    <a-drawer v-model:visible="mobileMenuVisible" placement="right" width="86vw" :header="false" :footer="false"
        :closable="false" :mask-closable="true" :esc-to-close="true" unmount-on-close class="hy-mobile-drawer">
        <div class="hy-mobile-panel">
            <div class="hy-mobile-panel__header">
                <a href="/" class="hy-mobile-panel__brand" aria-label="盒子云首页">
                    <img v-if="logoUrl" :src="logoUrl" alt="盒子云">
                </a>

                <button type="button" class="hy-mobile-panel__close" aria-label="关闭导航菜单" @click="closeMobileMenu">
                    <span></span>
                    <span></span>
                </button>
            </div>

            <div class="hy-mobile-panel__scroll">
                <nav class="hy-mobile-nav" aria-label="移动端主导航">
                    <div v-for="(item, index) in navItems" :key="item._key" class="hy-mobile-nav__group">
                        <button v-if="item.showArrow" type="button" class="hy-mobile-nav__main" :class="{
                            'is-active': displayedActiveIndex === index,
                            'is-open': mobileExpandedNavId === item._key
                        }" :aria-expanded="mobileExpandedNavId === item._key"
                            @click="toggleMobileNav(item)">
                            <span class="hy-mobile-nav__title">
                                {{ item.title }}
                                <em v-if="item.isRecommended">HOT</em>
                            </span>
                            <span class="hy-mobile-chevron" aria-hidden="true"></span>
                        </button>

                        <a v-else :href="item.url || '##'" class="hy-mobile-nav__main" :class="{
                            'is-active': displayedActiveIndex === index
                        }" @click="handleMobileLinkClick($event, item)">
                            <span class="hy-mobile-nav__title">
                                {{ item.title }}
                                <em v-if="item.isRecommended">HOT</em>
                            </span>
                            <span class="hy-mobile-chevron" aria-hidden="true" v-if="item.showArrow"></span>
                        </a>

                        <transition name="hy-mobile-collapse"
                            @before-enter="beforeMobileCollapseEnter"
                            @enter="enterMobileCollapse"
                            @after-enter="afterMobileCollapseEnter"
                            @before-leave="beforeMobileCollapseLeave"
                            @leave="leaveMobileCollapse"
                            @after-leave="afterMobileCollapseLeave">
                            <div v-if="mobileExpandedNavId === item._key" class="hy-mobile-nav__second-list">
                                <div v-if="item.children.length === 0" class="hy-mobile-nav__empty">
                                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
                                        aria-hidden="true">
                                        <path d="M7 4.5H15L18.5 8V18.5C18.5 19.33 17.83 20 17 20H7C6.17 20 5.5 19.33 5.5 18.5V6C5.5 5.17 6.17 4.5 7 4.5Z"
                                            stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" />
                                        <path d="M14.5 4.75V8.5H18.25M9 13H15M10 16H14" stroke="currentColor"
                                            stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                                    </svg>
                                    <span class="hy-mobile-nav__empty-content">
                                        <strong>暂无相关内容</strong>
                                    </span>
                                </div>

                                <div v-for="(secondItem, secondIndex) in item.children" :key="secondItem._key"
                                    class="hy-mobile-nav__second-group"
                                    :style="{ '--hy-mobile-item-index': secondIndex }">
                                    <button type="button" class="hy-mobile-nav__second" :class="[
                                        {
                                            'is-open': mobileExpandedSecondId === secondItem._key,
                                            'is-recommended': secondItem.isRecommended
                                        },
                                        `is-style-${secondItem.styleType}`
                                    ]" :aria-expanded="mobileExpandedSecondId === secondItem._key"
                                        @click="toggleMobileSecond(secondItem)">
                                        <span class="hy-mobile-nav__second-title">
                                            <span>{{ secondItem.title }}</span>
                                            <em v-if="secondItem.isRecommended">HOT</em>
                                        </span>
                                        <span class="hy-mobile-chevron" aria-hidden="true"></span>
                                    </button>

                                    <transition name="hy-mobile-collapse"
                                        @before-enter="beforeMobileCollapseEnter"
                                        @enter="enterMobileCollapse"
                                        @after-enter="afterMobileCollapseEnter"
                                        @before-leave="beforeMobileCollapseLeave"
                                        @leave="leaveMobileCollapse"
                                        @after-leave="afterMobileCollapseLeave">
                                        <div v-if="mobileExpandedSecondId === secondItem._key"
                                            class="hy-mobile-nav__products">
                                            <a v-for="(product, productIndex) in secondItem.products" :key="product._key"
                                                :href="product.url || '##'"
                                                :class="{ 'is-recommended': product.isRecommended }"
                                                :style="{ '--hy-mobile-item-index': productIndex }"
                                                @click="handleMobileLinkClick($event, product)">
                                                <span class="hy-mobile-nav__product-heading">
                                                    <span>{{ product.title }}</span>
                                                    <img v-if="product.isRecommended" :src="selectedIcon"
                                                        class="hy-mobile-nav__selected-image" alt="" aria-hidden="true">
                                                </span>
                                                <small v-if="product.hasTips">{{ product.tips }}</small>
                                            </a>

                                            <div v-if="secondItem.products.length === 0"
                                                class="hy-mobile-nav__empty hy-mobile-nav__empty--products">
                                                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
                                                    aria-hidden="true">
                                                    <path d="M7 4.5H15L18.5 8V18.5C18.5 19.33 17.83 20 17 20H7C6.17 20 5.5 19.33 5.5 18.5V6C5.5 5.17 6.17 4.5 7 4.5Z"
                                                        stroke="currentColor" stroke-width="1.5"
                                                        stroke-linejoin="round" />
                                                    <path d="M14.5 4.75V8.5H18.25M9 13H15M10 16H14"
                                                        stroke="currentColor" stroke-width="1.5"
                                                        stroke-linecap="round" stroke-linejoin="round" />
                                                </svg>
                                                <span class="hy-mobile-nav__empty-content">
                                                    <strong>暂无产品内容</strong>
                                                </span>
                                            </div>
                                        </div>
                                    </transition>
                                </div>
                            </div>
                        </transition>
                    </div>
                </nav>

                <!-- 只有这三个入口使用内联 SVG 图标 -->
                <div class="hy-mobile-quick-links">
                    <a href="/document.html" @click="closeMobileMenu">
                        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <path d="M6 3.75H14.5L18 7.25V20.25H6V3.75Z" stroke="currentColor" stroke-width="1.6"
                                stroke-linejoin="round" />
                            <path d="M14 3.75V7.75H18" stroke="currentColor" stroke-width="1.6"
                                stroke-linejoin="round" />
                            <path d="M8.75 11H15.25M8.75 14.5H15.25" stroke="currentColor" stroke-width="1.6"
                                stroke-linecap="round" />
                        </svg>
                        <span>{{ $t('common.documentationCenter') }}</span>
                    </a>

                    <a href="/home.htm" @click="closeMobileMenu">
                        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <rect x="3.75" y="4.5" width="16.5" height="12" rx="1.5" stroke="currentColor"
                                stroke-width="1.6" />
                            <path d="M9 20H15M12 16.5V20" stroke="currentColor" stroke-width="1.6"
                                stroke-linecap="round" />
                            <path d="M7 9L9 11L7 13M11.5 13H15.5" stroke="currentColor" stroke-width="1.6"
                                stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                        <span>{{ $t('common.console') }}</span>
                    </a>

                    <a v-if="!isLoggedIn" href="/login.htm" @click="closeMobileMenu">
                        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <path
                                d="M9.5 4.25H6.25C5.42 4.25 4.75 4.92 4.75 5.75V18.25C4.75 19.08 5.42 19.75 6.25 19.75H9.5"
                                stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
                            <path d="M13 8L17 12L13 16M8.5 12H17" stroke="currentColor" stroke-width="1.6"
                                stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                        <span>{{ $t('common.signIn') }}</span>
                    </a>

                    <a v-else href="/account.htm" class="hy-mobile-account-link" @click="closeMobileMenu">
                        <span>账户中心</span>
                    </a>
                </div>
            </div>

            <div class="hy-mobile-panel__footer">
                <button type="button" class="hy-mobile-language-button" @click="openMobileLanguageSelector">
                    <svg class="hy-mobile-language-button__globe" viewBox="0 0 20 20" fill="none"
                        xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <circle cx="10" cy="10" r="7.4" stroke="currentColor" stroke-width="1.45" />
                        <path d="M2.9 10H17.1" stroke="currentColor" stroke-width="1.35"
                            stroke-linecap="round" />
                        <path d="M10 2.65C12.1 4.65 13.25 7.2 13.25 10C13.25 12.8 12.1 15.35 10 17.35"
                            stroke="currentColor" stroke-width="1.35" stroke-linecap="round" />
                        <path d="M10 2.65C7.9 4.65 6.75 7.2 6.75 10C6.75 12.8 7.9 15.35 10 17.35"
                            stroke="currentColor" stroke-width="1.35" stroke-linecap="round" />
                    </svg>

                    <span class="hy-mobile-language-button__content">
                        <small>{{ $t('common.language') }}</small>
                        <strong>{{ currentLanguageLabel }}</strong>
                    </span>

                    <i class="hy-mobile-chevron" aria-hidden="true"></i>
                </button>

                <a v-if="!isLoggedIn" href="/login.htm" class="hy-mobile-register-wide">
                    {{ $t('common.signIn') }}
                </a>
            </div>
        </div>
    </a-drawer>

    <!-- Arco Design：移动端底部语言选择抽屉，与 PC 端共享 locale -->
    <a-drawer v-model:visible="mobileLanguageVisible" placement="bottom" :height="mobileLanguageDrawerHeight"
        :header="false" :footer="false" :closable="false" :mask-closable="true" :esc-to-close="true"
        unmount-on-close class="hy-language-drawer">
        <div class="hy-mobile-language-sheet">
            <div class="hy-mobile-language-sheet__handle"></div>

            <div class="hy-mobile-language-sheet__heading">
                <div>
                    <strong>{{ $t('common.language') }}</strong>
                    <small>{{ currentLanguageLabel }}</small>
                </div>

                <button type="button" aria-label="关闭语言选择" @click="mobileLanguageVisible = false">
                    <span></span>
                    <span></span>
                </button>
            </div>

            <div class="hy-mobile-language-sheet__list">
                <button v-for="lang in languageOptions" :key="lang.value" type="button" :class="{
                    'is-active': lang.value === currentLocale
                }" @click="handleMobileLanguageSelect(lang.value)">
                    <span class="hy-mobile-language-sheet__option">
                        <strong>{{ lang.label }}</strong>
                        <small>{{ lang.code }}</small>
                    </span>

                    <span v-if="lang.value === currentLocale" class="hy-mobile-language-check"
                        aria-hidden="true"></span>
                </button>
            </div>
        </div>
    </a-drawer>

    <Loading :HeziyunData="HeziyunData" />
</template>

<style scoped>
.hy-header {
    --hy-primary: #1677ff;
    --hy-primary-dark: #0868f4;
    --hy-text: #172033;
    --hy-text-secondary: #3c4659;
    --hy-border: rgba(221, 229, 241, 0.76);
    --hy-header-duration: 420ms;
    --hy-header-easing: cubic-bezier(0.22, 1, 0.36, 1);

    position: fixed;
    z-index: 1000;
    top: 0;
    right: 0;
    left: 0;

    height: 3.6rem;

    background: transparent;
    border: 1px solid var(--hy-border);
    border-radius: 0;

    box-shadow:
        0 6px 22px rgba(31, 68, 122, 0.07),
        0 1px 3px rgba(31, 68, 122, 0.04);

    backdrop-filter: blur(18px);
    -webkit-backdrop-filter: blur(18px);

    transition:
        top var(--hy-header-duration) var(--hy-header-easing),
        right var(--hy-header-duration) var(--hy-header-easing),
        left var(--hy-header-duration) var(--hy-header-easing),
        height var(--hy-header-duration) var(--hy-header-easing),
        border-radius var(--hy-header-duration) var(--hy-header-easing),
        box-shadow 320ms ease,
        border-color 320ms ease;
}

/*
 * 使用两层伪元素交叉淡化背景。
 * 直接从纯色切换到 linear-gradient 时浏览器通常无法平滑插值，
 * 这样处理可以避免背景在切换瞬间闪一下。
 */
.hy-header::before,
.hy-header::after {
    position: absolute;
    z-index: -1;
    inset: 0;

    border-radius: inherit;

    content: "";
    pointer-events: none;

    transition:
        opacity 320ms ease,
        border-radius var(--hy-header-duration) var(--hy-header-easing);
}

.hy-header::before {
    background-color: #ffffffa1;
    opacity: 1;
}

.hy-header::after {
    background:
        linear-gradient(100deg,
            rgba(249, 252, 255, 0.98) 0%,
            rgba(255, 255, 255, 0.98) 43%,
            rgba(252, 254, 255, 0.98) 100%);
    opacity: 0;
}

/* 页面离开顶部后的悬浮卡片状态 */
.hy-header.is-scrolled {
    top: 15px;
    right: 50px;
    left: 50px;

    height: 64px;
    border-radius: 12px;
}

.hy-header.is-scrolled::before {
    opacity: 0;
}

.hy-header.is-scrolled::after {
    opacity: 1;
}

.hy-header__inner {
    display: flex;
    align-items: center;

    width: 100%;
    height: 100%;

    padding: 0 25px;
}

/* ==============================
   Logo
============================== */

.hy-brand {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: flex-start;

    width: 140px;
    height: 100%;

    color: inherit;
    text-decoration: none;
}

.hy-brand__image {
    display: block;

    width: auto;
    max-width: 100%;
    height: 100%;

    object-fit: contain;
    object-position: left center;
}

/* ==============================
   一级导航
============================== */

.hy-navigation {
    display: flex;
    align-self: stretch;
    flex: 0 1 auto;
    align-items: stretch;
    gap: 25px;

    margin-left: 30px;
}

.hy-navigation__item {
    position: relative;

    display: inline-flex;
    align-items: center;
    gap: 4px;

    min-width: max-content;
    height: 100%;

    color: var(--hy-text);

    font-family: '思源黑';
    font-size: 14px;
    font-weight: 500;
    line-height: 1;

    text-decoration: none;
    white-space: nowrap;

    transition:
        color 200ms ease,
        transform 220ms cubic-bezier(0.22, 1, 0.36, 1);
}

.hy-navigation__item::after {
    position: absolute;
    right: 0;
    bottom: 7px;
    left: 0;

    height: 2px;

    background: var(--hy-primary);
    border-radius: 999px;

    content: "";
    opacity: 0;
    transform: scaleX(0.25);
    transform-origin: center;
    transition:
        opacity 200ms ease,
        transform 240ms cubic-bezier(0.22, 1, 0.36, 1);
}

.hy-navigation__item:hover,
.hy-navigation__item.is-active {
    color: var(--hy-primary);
}

.hy-navigation__item.is-active {
    font-weight: 500;
}

.hy-navigation__item.is-active::after {
    opacity: 1;
    transform: scaleX(1);
}

.hy-navigation__text {
    position: relative;
    z-index: 1;
}

.hy-navigation__arrow {
    display: block;
    flex: 0 0 auto;

    width: 10px;
    height: 10px;

    color: currentColor;
}

.hy-navigation__hot {
    position: absolute;
    top: 13px;
    right: -40%;

    min-width: 23px;
    height: 13px;
    padding: 0 4px;

    color: #fff;
    background: linear-gradient(135deg, #ff3d46, #ff1728);
    border: 1px solid rgba(255, 255, 255, 0.9);
    border-radius: 4px;

    box-shadow: 0 2px 5px rgba(255, 36, 51, 0.22);

    font-family: Arial, sans-serif;
    font-size: 7.5px;
    font-weight: 700;
    line-height: 11px;
    text-align: center;
}

/* ==============================
   右侧功能区
============================== */

.hy-actions {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 24px;

    height: 100%;
    margin-left: auto;
}

.hy-actions__link {
    position: relative;

    display: inline-flex;
    align-items: center;
    gap: 6px;

    min-width: max-content;
    height: 34px;

    color: #222b3c;


    font-family: '思源黑';
    font-size: 13px;
    font-weight: 500;
    line-height: 34px;

    text-decoration: none;
    white-space: nowrap;

    transition: color 200ms ease;
}

.hy-actions__link:hover {
    color: var(--hy-primary);
}

.hy-actions__icon {
    display: block;
    flex: 0 0 auto;
    width: 18px;
    height: 18px;
    color: currentColor;
}

/* 注册按钮 */
.hy-register {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    gap: 4px;

    width: 105px;
    height: 35px;

    color: #fff;
    background: linear-gradient(135deg, #1981ff 0%, #095ff0 100%);
    border: 1px solid rgba(24, 116, 255, 0.68);
    border-radius: 8px;

    box-shadow:
        0 5px 12px rgba(14, 103, 241, 0.2),
        inset 0 1px 0 rgba(255, 255, 255, 0.18);


    font-family: '思源黑';
    font-size: 13px;
    font-weight: 500;
    line-height: 30px;

    text-decoration: none;
    white-space: nowrap;

    transition:
        transform 220ms cubic-bezier(0.22, 1, 0.36, 1),
        box-shadow 220ms ease,
        filter 200ms ease;
}

.hy-register:hover {
    color: #fff;

    box-shadow:
        0 7px 16px rgba(14, 103, 241, 0.26),
        inset 0 1px 0 rgba(255, 255, 255, 0.2);

    filter: brightness(1.03);
    transform: translateY(-1px);
}

.hy-register:active {
    transform: translateY(0);
}

.hy-register__icon {
    width: 15px;
    height: 15px;
}

/* 已登录用户 */
.hy-user {
    display: inline-flex;
    align-items: center;
    gap: 7px;

    max-width: 120px;
    height: 38px;

    color: #202a3a;
    text-decoration: none;
}

.hy-user__avatar {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;

    width: 35px;
    height: 35px;

    background: #edf4ff;
    border: 1px solid #dbe8fb;
    border-radius: 50%;

    object-fit: cover;
}

.hy-user__avatar--text {
    color: var(--hy-primary);

    font-size: 12px;
    font-weight: 600;
}

.hy-user__name {
    overflow: hidden;

    color: #202a3a;

    font-size: 12px;
    font-weight: 500;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* ==============================
   移动端头部与抽屉
============================== */

.hy-mobile-actions {
    display: none;
    align-items: center;
    gap: 12px;
    margin-left: auto;
}

.hy-mobile-register {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 34px;
    padding: 0 14px;
    color: #fff;
    background: linear-gradient(135deg, #1981ff 0%, #075ff1 100%);
    border-radius: 7px;
    box-shadow: 0 6px 14px rgba(12, 101, 242, 0.2);
    font-size: 13px;
    font-weight: 600;
    text-decoration: none;
    white-space: nowrap;
}

.hy-mobile-user {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    overflow: hidden;
    color: var(--hy-primary);
    background: #edf4ff;
    border: 1px solid #dbe8fb;
    border-radius: 50%;
    font-size: 12px;
    font-weight: 700;
    text-decoration: none;
}

.hy-mobile-user img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.hy-mobile-menu-button {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    width: 34px;
    height: 34px;
    padding: 0;
    background: transparent;
    border: 0;
    border-radius: 7px;
    cursor: pointer;
}

.hy-mobile-menu-button span {
    display: block;
    width: 19px;
    height: 1.7px;
    background: #172033;
    border-radius: 999px;
}

.hy-mobile-panel {
    --hy-primary: #1677ff;

    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    min-height: 100%;
    color: #172033;
    background: #fff;
}

.hy-mobile-panel__header {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: space-between;
    height: 60px;
    padding: 0 20px;
    border-bottom: 1px solid #edf0f5;
}

.hy-mobile-panel__brand {
    display: flex;
    align-items: center;
    min-width: 0;
    height: 44px;
    color: inherit;
    text-decoration: none;
}

.hy-mobile-panel__brand>img {
    display: block;
    width: auto;
    max-width: 126px;
    object-fit: contain;
    object-position: left center;
}

.hy-mobile-panel__close {
    position: relative;
    width: 36px;
    height: 36px;
    padding: 0;
    background: transparent;
    border: 0;
    border-radius: 8px;
    cursor: pointer;
}

.hy-mobile-panel__close span {
    position: absolute;
    top: 17px;
    left: 8px;
    width: 18px;
    height: 1.7px;
    background: #273244;
    border-radius: 999px;
}

.hy-mobile-panel__close span:first-child {
    transform: rotate(45deg);
}

.hy-mobile-panel__close span:last-child {
    transform: rotate(-45deg);
}

.hy-mobile-panel__scroll {
    flex: 1;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior: contain;
    padding: 8px 18px 18px;
}

.hy-mobile-nav__group:not(:last-child) {
    border-bottom: 1px solid #edf0f5;
}

.hy-mobile-nav__main,
.hy-mobile-nav__second {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    min-height: 50px;
    padding: 0 8px;
    color: #20293a;
    background: transparent;
    border: 0;
    font-family: inherit;
    font-size: 15px;
    font-weight: 500;
    text-align: left;
    text-decoration: none;
    cursor: pointer;
}

.hy-mobile-nav__main.is-active,
.hy-mobile-nav__main.is-open {
    color: var(--hy-primary);
}

.hy-mobile-nav__title {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
}

.hy-mobile-nav__title em {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 28px;
    height: 16px;
    padding: 0 5px;
    color: #fff;
    background: #ff2d3a;
    border-radius: 4px;
    font-family: Arial, sans-serif;
    font-size: 8px;
    font-style: normal;
    font-weight: 700;
}

.hy-mobile-chevron {
    display: inline-block;
    flex: 0 0 auto;
    width: 8px;
    height: 8px;
    margin-right: 3px;
    border-top: 1.5px solid currentColor;
    border-right: 1.5px solid currentColor;
    opacity: 0.62;
    transform: rotate(45deg);
    transition: transform 220ms cubic-bezier(0.22, 1, 0.36, 1);
}

.hy-mobile-nav__main.is-open .hy-mobile-chevron,
.hy-mobile-nav__second.is-open .hy-mobile-chevron {
    transform: rotate(135deg);
}

.hy-mobile-nav__second-list {
    padding: 0 0 6px 10px;
    background: #fbfcfe;
    border-radius: 9px;
}

.hy-mobile-nav__second-group+.hy-mobile-nav__second-group {
    border-top: 1px solid #eef1f5;
}

.hy-mobile-nav__second {
    min-height: 49px;
    padding-right: 10px;
    color: #3a4556;
    font-size: 14px;
}

.hy-mobile-nav__second.is-open {
    color: var(--hy-primary);
}

.hy-mobile-nav__second.is-style-2.is-open {
    background: #f2f7ff;
    border-radius: 7px;
    font-weight: 600;
}

.hy-mobile-nav__second-title {
    display: inline-flex;
    min-width: 0;
    align-items: center;
    gap: 7px;
}

.hy-mobile-nav__second-title > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.hy-mobile-nav__second-title em {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    height: 15px;
    padding: 0 4px;
    color: #ff3548;
    background: #fff1f2;
    border: 1px solid #ffd7dc;
    border-radius: 3px;
    font-family: Arial, sans-serif;
    font-size: 8px;
    font-style: normal;
    font-weight: 700;
    line-height: 1;
}

.hy-mobile-nav__products {
    display: grid;
    gap: 2px;
    padding: 0 10px 10px;
}

.hy-mobile-nav__products a {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 10px 12px;
    color: #2b3546;
    background: #fff;
    border-radius: 7px;
    text-decoration: none;
}

.hy-mobile-nav__products a:active {
    background: #f1f6ff;
}

.hy-mobile-nav__products span {
    font-size: 13px;
    font-weight: 500;
}

.hy-mobile-nav__product-heading {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 7px;
}

.hy-mobile-nav__product-heading > span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.hy-mobile-nav__selected-image {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    right: 0;
    width: 90px;
    object-fit: contain;
    pointer-events: none;
    user-select: none;
}

.hy-mobile-nav__products a.is-recommended {
    position: relative;
    padding-right: 75px;
    background: url('/src/static/img/header/hot-back.webp') no-repeat center;
    background-size: cover;
    background-color: #fff;
}

.hy-mobile-nav__products small {
    overflow: hidden;
    color: #8a94a6;
    font-size: 11px;
    line-height: 16px;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    text-overflow: ellipsis;
}

/* 移动端菜单空状态：避免点击后无展开、无文字反馈 */
.hy-mobile-nav__empty {
    display: flex;
    align-items: center;
    gap: 11px;
    min-height: 50px;
    margin: 8px 10px 10px 0;
    padding: 12px 14px;
    color: #748198;
    background: #fff;
}

.hy-mobile-nav__empty--products {
    margin: 4px 2px 2px;
}

.hy-mobile-nav__empty>svg {
    flex: 0 0 auto;
    width: 24px;
    height: 24px;
    color: #9aa8ba;
}

.hy-mobile-nav__empty-content {
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 2px;
}

.hy-mobile-nav__empty-content strong {
    color: #4b586b;
    font-size: 13px;
    font-weight: 600;
    line-height: 18px;
}

.hy-mobile-nav__empty-content small {
    overflow: visible;
    color: #929daf;
    font-size: 11px;
    font-weight: 400;
    line-height: 16px;
    text-overflow: initial;
    white-space: normal;
}

.hy-mobile-quick-links {
    border-top: 1px solid #edf0f5;
}

.hy-mobile-quick-links a {
    display: grid;
    grid-template-columns: 24px minmax(0, 1fr) auto;
    align-items: center;
    gap: 13px;
    min-height: 55px;
    padding: 0 8px;
    color: #20293a;
    border-bottom: 1px solid #edf0f5;
    font-size: 14px;
    font-weight: 500;
    text-decoration: none;
}

.hy-mobile-quick-links svg {
    width: 21px;
    height: 21px;
    color: #52627a;
}

.hy-mobile-quick-links .hy-mobile-account-link {
    grid-template-columns: minmax(0, 1fr) auto;
}

.hy-mobile-panel__footer {
    flex: 0 0 auto;
    padding: 14px 18px calc(16px + env(safe-area-inset-bottom));
    background: #fff;
    border-top: 1px solid #edf0f5;
}

.hy-mobile-language-button {
    display: grid;
    grid-template-columns: 22px minmax(0, 1fr) auto;
    align-items: center;
    gap: 11px;
    width: 100%;
    min-height: 52px;
    padding: 7px 14px;
    color: #334055;
    background: linear-gradient(180deg, #f9fbfe 0%, #f5f8fc 100%);
    border: 1px solid #e9eef5;
    border-radius: 10px;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.85);
    font-family: inherit;
    text-align: left;
    cursor: pointer;
    transition:
        background-color 180ms ease,
        border-color 180ms ease,
        transform 180ms cubic-bezier(0.22, 1, 0.36, 1);
}

.hy-mobile-language-button:active {
    background: #eef5ff;
    border-color: #dbe8fb;
    transform: scale(0.992);
}

.hy-mobile-language-button__globe {
    width: 20px;
    height: 20px;
    color: #53647c;
}

.hy-mobile-language-button__content {
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 1px;
}

.hy-mobile-language-button__content small {
    color: #8a94a6;
    font-size: 10px;
    font-weight: 400;
    line-height: 14px;
}

.hy-mobile-language-button__content strong {
    overflow: hidden;
    color: #283548;
    font-size: 14px;
    font-weight: 600;
    line-height: 19px;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.hy-mobile-register-wide {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 48px;
    margin-top: 12px;
    color: #fff;
    background: linear-gradient(135deg, #1981ff 0%, #075ff1 100%);
    border-radius: 9px;
    box-shadow: 0 8px 20px rgba(12, 101, 242, 0.22);
    font-size: 15px;
    font-weight: 600;
    text-decoration: none;
}

.hy-mobile-language-sheet {
    --hy-primary: #1677ff;

    min-height: 100%;
    padding: 10px 18px calc(22px + env(safe-area-inset-bottom));
    background: #fff;
    border-radius: 20px 20px 0 0;
}

.hy-mobile-language-sheet__handle {
    width: 42px;
    height: 4px;
    margin: 0 auto 18px;
    background: #d7dce5;
    border-radius: 999px;
}

.hy-mobile-language-sheet__heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 44px;
    margin-bottom: 9px;
}

.hy-mobile-language-sheet__heading>div {
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 2px;
}

.hy-mobile-language-sheet__heading strong {
    color: #172033;
    font-size: 16px;
    font-weight: 700;
    line-height: 22px;
}

.hy-mobile-language-sheet__heading small {
    color: #8a94a6;
    font-size: 11px;
    line-height: 16px;
}

.hy-mobile-language-sheet__heading>button {
    position: relative;
    flex: 0 0 auto;
    width: 34px;
    height: 34px;
    padding: 0;
    background: #f6f8fb;
    border: 0;
    border-radius: 9px;
    cursor: pointer;
}

.hy-mobile-language-sheet__heading>button span {
    position: absolute;
    top: 16px;
    left: 9px;
    width: 16px;
    height: 1.5px;
    background: #536071;
    border-radius: 999px;
}

.hy-mobile-language-sheet__heading>button span:first-child {
    transform: rotate(45deg);
}

.hy-mobile-language-sheet__heading>button span:last-child {
    transform: rotate(-45deg);
}

.hy-mobile-language-sheet__list {
    display: grid;
    gap: 6px;
}

.hy-mobile-language-sheet__list>button {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    min-height: 52px;
    padding: 7px 14px;
    color: #3b4658;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 10px;
    font-family: inherit;
    text-align: left;
    cursor: pointer;
    transition:
        color 180ms ease,
        background-color 180ms ease,
        border-color 180ms ease,
        transform 180ms cubic-bezier(0.22, 1, 0.36, 1);
}

.hy-mobile-language-sheet__list>button:active {
    transform: scale(0.992);
}

.hy-mobile-language-sheet__list>button.is-active {
    color: var(--hy-primary);
    background: #f2f7ff;
    border-color: #e0ebff;
}

.hy-mobile-language-sheet__option {
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 1px;
}

.hy-mobile-language-sheet__option strong {
    overflow: hidden;
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.hy-mobile-language-sheet__option small {
    color: #9aa4b5;
    font-family: Arial, sans-serif;
    font-size: 10px;
    line-height: 14px;
}

.hy-mobile-language-check {
    position: relative;
    flex: 0 0 auto;
    width: 20px;
    height: 20px;
    margin-left: 12px;
    background: #e5efff;
    border-radius: 50%;
}

.hy-mobile-language-check::before {
    position: absolute;
    top: 5px;
    left: 5px;
    width: 9px;
    height: 5px;
    border-bottom: 2px solid var(--hy-primary);
    border-left: 2px solid var(--hy-primary);
    content: "";
    transform: rotate(-45deg);
}

.hy-mobile-collapse-enter-active,
.hy-mobile-collapse-leave-active {
    overflow: hidden;
    transition:
        height 320ms cubic-bezier(0.22, 1, 0.36, 1),
        opacity 210ms ease,
        transform 300ms cubic-bezier(0.22, 1, 0.36, 1);
    transform-origin: top center;
}

.hy-mobile-collapse-enter-from,
.hy-mobile-collapse-leave-to {
    height: 0;
    opacity: 0;
    transform: translate3d(0, -8px, 0);
}

.hy-mobile-collapse-enter-active .hy-mobile-nav__second-group,
.hy-mobile-collapse-enter-active .hy-mobile-nav__products>a {
    animation: hy-mobile-item-in 260ms cubic-bezier(0.22, 1, 0.36, 1) both;
    animation-delay: calc(var(--hy-mobile-item-index, 0) * 28ms + 35ms);
}

@keyframes hy-mobile-item-in {
    from {
        opacity: 0;
        transform: translate3d(0, -5px, 0);
    }

    to {
        opacity: 1;
        transform: translate3d(0, 0, 0);
    }
}

:global(.hy-mobile-drawer .arco-drawer),
:global(.hy-language-drawer .arco-drawer) {
    overflow: hidden;
    background: transparent;
    box-shadow: none;
}

:global(.hy-mobile-drawer .arco-drawer) {
    border-radius: 15px 0 0 0;
}

:global(.hy-language-drawer .arco-drawer) {
    border-radius: 20px 20px 0 0;
}

:global(.hy-mobile-drawer .arco-drawer-body),
:global(.hy-language-drawer .arco-drawer-body) {
    height: 100%;
    padding: 0;
}

:global(.hy-mobile-drawer .arco-drawer-mask),
:global(.hy-language-drawer .arco-drawer-mask) {
    background: rgba(17, 27, 43, 0.46);
    backdrop-filter: blur(2px);
    -webkit-backdrop-filter: blur(2px);
}

:global(.hy-mobile-drawer .arco-drawer),
:global(.hy-language-drawer .arco-drawer),
:global(.hy-mobile-drawer .arco-drawer-mask),
:global(.hy-language-drawer .arco-drawer-mask) {
    will-change: left, transform, opacity;
}

/* ==============================
   中等屏幕
============================== */

@media screen and (max-width: 1160px) {
    .hy-header__inner {
        padding-right: 20px;
        padding-left: 20px;
    }

    .hy-navigation {
        gap: 22px;
        margin-left: 35px;
    }

    .hy-actions {
        gap: 17px;
    }
}

@media screen and (max-width: 1020px) {
    .hy-navigation {
        gap: 17px;
        margin-left: 25px;
    }

    .hy-document,
    .hy-console {
        display: none;
    }
}

/* ==============================
   移动端适配
============================== */

@media screen and (max-width: 768px) {
    .hy-header {
        height: 15vw;
        border-right: 0;
        border-left: 0;
        box-shadow: 0 5px 18px rgba(31, 68, 122, 0.07);
    }

    .hy-header.is-scrolled {
        top: 0px;
        right: 0px;
        left: 0px;
        height: 15vw;
        border-radius: 0;
    }

    .hy-header__inner {
        padding: 0 14px;
    }

    .hy-brand {
        width: auto;
        max-width: calc(100vw - 172px);
    }

    .hy-brand__image {
        max-width: 108px;
    }

    .hy-navigation,
    .hy-actions,
    .hy-mega-menu {
        display: none;
    }

    .hy-mobile-actions {
        display: flex;
    }
}

@media screen and (max-width: 390px) {
    .hy-header__inner {
        padding: 0 11px;
    }

    .hy-brand__image {
        max-width: 35vw;
    }

    .hy-mobile-actions {
        gap: 8px;
    }

    .hy-mobile-register {
        height: 32px;
        padding: 0 11px;
        font-size: 12px;
    }

    .hy-mobile-menu-button {
        width: 32px;
        height: 32px;
    }
}

/* =====================================
   产品中心下拉菜单
===================================== */

.hy-mega-menu {
    position: absolute;
    z-index: 1002;
    top: calc(100% + 4px);
    left: 12px;

    display: grid;
    grid-template-columns: 190px minmax(0, 1fr);

    width: min(830px, calc(100% - 24px));
    min-width: 0;
    max-width: 830px;
    height: 252px;

    overflow: hidden;

    background:
        linear-gradient(110deg,
            rgba(255, 255, 255, 0.99) 0%,
            rgba(253, 254, 255, 0.98) 100%);

    border: 1px solid rgba(224, 232, 243, 0.75);
    border-radius: 10px;

    box-shadow:
        0 16px 42px rgba(36, 67, 111, 0.11),
        0 4px 12px rgba(36, 67, 111, 0.05);

    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);

    /*
     * 下拉层保持为头部的绝对定位子元素。
     * 头部在滚动时改变 top、left、height，下拉层会随父级同帧移动，
     * 同时保留自身进入/离开的位移淡入动画。
     */
    will-change: transform, opacity;
}

/* 左侧分类 */

.hy-mega-menu__aside {
    position: relative;

    padding: 18px 12px;

    background:
        linear-gradient(180deg,
            rgba(255, 255, 255, 0.99),
            rgba(252, 254, 255, 0.99));

    border-right: 1px solid rgba(228, 234, 243, 0.86);
        overflow-y: auto;
}
/* 隐藏滚动条 */
 .hy-mega-menu__aside::-webkit-scrollbar { 
    display: none;
 }

.hy-mega-menu__category-list {
    display: flex;
    flex-direction: column;
    gap: 1px;

    width: 100%;
}

.hy-mega-menu__category {
    position: relative;

    display: flex;
    align-items: center;

    width: 100%;
    height: 35px;
    padding: 0 11px;

    color: #263244;
    background: transparent;
    border: 0;
    border-radius: 5px;

    font-family: '思源黑', "PingFang SC", "Microsoft YaHei", sans-serif;
    font-size: 13px;
    font-weight: 500;
    line-height: 1;

    cursor: pointer;
    outline: none;

    transition:
        color 200ms ease,
        background-color 220ms ease,
        transform 220ms cubic-bezier(0.22, 1, 0.36, 1);
}

.hy-mega-menu__category:hover,
.hy-mega-menu__category:focus-visible,
.hy-mega-menu__category.is-active {
    color: #1677ff;
    background-color: #f4f4f4;
}

.hy-mega-menu__category:active {
    transform: scale(0.99);
}

.hy-mega-menu__category-title {
    display: flex;
    flex: 1;
    min-width: 0;
    align-items: center;
    gap: 7px;
    overflow: hidden;
    text-align: left;
}

.hy-mega-menu__category-text {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.hy-mega-menu__hot {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    height: 16px;
    padding: 0 5px;
    color: #ff3548;
    background: #fff1f2;
    border: 1px solid #ffd7dc;
    border-radius: 3px;
    font-family: Arial, sans-serif;
    font-size: 8px;
    font-style: normal;
    font-weight: 700;
    line-height: 1;
}

.hy-mega-menu__category.is-style-2.is-active {
    color: #1664ff;
    background: #eef5ff;
    font-weight: 600;
}

.hy-mega-menu__category-arrow {
    flex: 0 0 auto;

    width: 13px;
    height: 13px;
    margin-left: 8px;

    color: #4f5d70;

    transition:
        color 200ms ease,
        transform 220ms cubic-bezier(0.22, 1, 0.36, 1);
}

.hy-mega-menu__category:hover .hy-mega-menu__category-arrow,
.hy-mega-menu__category.is-active .hy-mega-menu__category-arrow {
    color: #1677ff;
    transform: translateX(1px);
}

/* 右侧产品 */
.hy-mega-menu__ceontent {
    height: 100%;
    overflow-y: auto;
}

.hy-mega-menu__ceontent::-webkit-scrollbar { 
    display: none;
 }

.hy-mega-menu__products {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    grid-auto-rows: 1fr;
    gap: 10px;

    min-width: 0;
    padding: 13px 20px;
    height: fit-content;
}

.hy-mega-menu__product {
    position: relative;

    display: flex;
    align-items: center;

    min-width: 0;
    padding: 10px 15px;

    color: #172033;
    text-decoration: none;

    transition:
        background-color 220ms ease,
        transform 220ms cubic-bezier(0.22, 1, 0.36, 1);
    border-radius: 9px;
    background: #fff;
    box-shadow: 0 2px 6px rgba(22, 44, 82, .025);
    border: 1px solid rgba(228, 234, 243, 0.8);
}

/* 每行三个：第 1、2 列显示竖向分隔线 */
/* .hy-mega-menu__product:not(:nth-child(3n)) {
    border-right: 1px solid rgba(228, 234, 243, 0.8);
} */

/* 两行布局时，第一行显示横向分隔线 */
/* .hy-mega-menu__product:not(:nth-last-child(-n + 3)) {
    border-bottom: 1px solid rgba(228, 234, 243, 0.8);
} */

.hy-mega-menu__product:hover {
    z-index: 1;
    background-color: rgb(244 244 244 / 80%);
}

.hy-mega-menu__product-content {
    display: flex;
    flex: 1;
    flex-direction: column;
    justify-content: center;
    
    min-width: 0;
}

.hy-mega-menu__product-heading {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 7px;
}

.hy-mega-menu__product-title {
    display: block;
    min-width: 0;
    flex: 1;

    overflow: hidden;

    color: #172033;

    font-family: '思源黑', "PingFang SC", "Microsoft YaHei", sans-serif;
    font-size: 13.5px;
    line-height: 19px;

    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 500;
    position: relative;
    z-index: 1;
}

.hy-mega-menu__selected-image {
    position: absolute;
    right: -12px;
    top: 15px;
    z-index: 0;
    width: 90px;
    object-fit: contain;
    pointer-events: none;
    user-select: none;
    opacity: .96;
    transform: translateY(-50%);
    transform-origin: center;
    transition: transform 220ms cubic-bezier(0.22, 1, 0.36, 1), opacity 180ms ease;
}

.hy-mega-menu__product.is-recommended:hover .hy-mega-menu__selected-image {
    opacity: 1;
    transform: translateY(-50%) scale(1.05);
}

.hy-mega-menu__ceontent.is-style-2 .hy-mega-menu__selected-image {
    width: 90px;
    height: 90px;
}

.hy-mega-menu__product.is-recommended {
    border-color: #dceaff;
    
    background: url('/src/static/img/header/hot-back.webp') no-repeat center;
    background-size: cover;
}

.hy-mega-menu__ceontent.is-style-2 .hy-mega-menu__products {
    grid-template-columns: repeat(2, minmax(0, 1fr));
}

.hy-mega-menu__ceontent.is-style-2 .hy-mega-menu__product {
    min-height: 78px;
}

.hy-mega-menu__product-description {
    display: block;

    max-width: 100%;
    margin-top: 5px;
    overflow: hidden;

    color: #8a94a6;

    font-family: '思源黑', "PingFang SC", "Microsoft YaHei", sans-serif;
    font-size: 10.5px;
    font-weight: 400;
    line-height: 16px;

    text-overflow: ellipsis;
    white-space: nowrap;
    position: relative;
    z-index: 1;
}

.hy-mega-menu__empty {
    display: flex;
    grid-column: 1 / -1;
    align-items: center;
    justify-content: center;

    color: #98a2b3;

    font-size: 13px;
        padding-top: 100px;
}

/* 产品分类切换动画：只作用于现有产品区域 */

.hy-products-enter-active,
.hy-products-leave-active {
    transition:
        opacity 180ms ease,
        transform 220ms cubic-bezier(0.22, 1, 0.36, 1);
    will-change: opacity, transform;
}

.hy-products-enter-from {
    opacity: 0;
    transform: translate3d(8px, 0, 0);
}

.hy-products-leave-to {
    opacity: 0;
    transform: translate3d(-5px, 0, 0);
}

/* 下拉进入与离开动画 */

.hy-dropdown-enter-active {
    transition:
        opacity 240ms ease,
        transform 260ms cubic-bezier(0.22, 1, 0.36, 1),
        filter 220ms ease;
    transform-origin: top center;
}

.hy-dropdown-leave-active {
    transition:
        opacity 160ms ease,
        transform 180ms cubic-bezier(0.4, 0, 1, 1),
        filter 160ms ease;
    transform-origin: top center;
}

.hy-dropdown-enter-from,
.hy-dropdown-leave-to {
    opacity: 0;
    filter: blur(1px);
    transform: translateY(-5px) scale(0.99);
}

.hy-dropdown-enter-to,
.hy-dropdown-leave-from {
    opacity: 1;
    filter: blur(0);
    transform: translateY(0) scale(1);
}

/* 中等宽度适配 */

@media screen and (max-width: 1160px) {
    .hy-mega-menu {
        width: min(780px, calc(100% - 24px));
        grid-template-columns: 180px minmax(0, 1fr);
    }
}

@media screen and (max-width: 1020px) {
    .hy-mega-menu {
        width: min(720px, calc(100% - 24px));
        grid-template-columns: 170px minmax(0, 1fr);
    }
}

/* 移动端不显示 PC 下拉菜单 */

@media screen and (max-width: 768px) {
    .hy-mega-menu {
        display: none;
    }
}

/* 尊重系统的“减少动态效果”设置 */
@media (prefers-reduced-motion: reduce) {

    .hy-header,
    .hy-header::before,
    .hy-header::after,
    .hy-products-enter-active,
    .hy-products-leave-active,
    .hy-dropdown-enter-active,
    .hy-dropdown-leave-active,
    .hy-mobile-collapse-enter-active,
    .hy-mobile-collapse-leave-active,
    .hy-mobile-language-button,
    .hy-mobile-language-sheet__list>button {
        transition-duration: 1ms !important;
    }

    .hy-mobile-collapse-enter-active .hy-mobile-nav__second-group,
    .hy-mobile-collapse-enter-active .hy-mobile-nav__products>a {
        animation-duration: 1ms !important;
        animation-delay: 0ms !important;
    }
}
</style>